import React from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageMeta from '../components/PageMeta';

const TermsConditions = () => {
  return (
    <div className="bg-black min-h-screen pt-32 md:pt-48">
      <PageMeta 
        title="Terms & Conditions | Living Media Video Editing Agency"
        description="Service agreement for Living Media. Understand our policies on video editing deliverables, revisions, payments, and client usage rights."
      />
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 pb-24 md:pb-32">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-[2px] bg-[#BEF264]" />
            <span className="text-[#BEF264] font-black uppercase tracking-[0.4em] text-[10px]">
              Service Agreement
            </span>
          </div>
          <h1 className="text-5xl md:text-8xl font-black text-white tracking-tighter leading-none mb-16 uppercase italic">
            TERMS & <br /><span className="not-italic text-[#BEF264]">CONDITIONS.</span>
          </h1>

          <div className="grid lg:grid-cols-12 gap-12 border-t border-white/10 pt-16">
            <aside className="lg:col-span-4">
              <div className="sticky top-32 space-y-4">
                <p className="text-white/40 font-black uppercase tracking-widest text-[10px]">Last updated</p>
                <p className="text-white font-medium">January 2026</p>
                <div className="h-px bg-white/5 w-full my-8" />
                <p className="text-white/40 font-black uppercase tracking-widest text-[10px]">Binding Agreement</p>
                <p className="text-white font-medium">Standard Commercial Terms</p>
              </div>
            </aside>

            <div className="lg:col-span-8 space-y-12">
              <section className="space-y-6">
                <p className="text-white/70 leading-relaxed text-lg font-medium tracking-tight">
                  These Terms & Conditions govern your use of Living Media’s website and services. By engaging with us, you agree to the terms outlined below.
                </p>
              </section>

              <section className="space-y-6">
                <h2 className="text-2xl font-black text-white uppercase tracking-tight italic">Services</h2>
                <p className="text-white/70 leading-relaxed text-lg font-medium tracking-tight">
                  Living Media provides professional video editing and related creative services. Scope, turnaround times, and deliverables are agreed upon before work begins.
                </p>
              </section>

              <section className="space-y-6">
                <h2 className="text-2xl font-black text-white uppercase tracking-tight italic">Free Trials</h2>
                <p className="text-white/70 leading-relaxed text-lg font-medium tracking-tight">
                  Free trials are offered at our discretion and may be withdrawn or limited at any time. Trial work is provided in good faith and does not guarantee ongoing service.
                </p>
              </section>

              <section className="space-y-6">
                <h2 className="text-2xl font-black text-white uppercase tracking-tight italic">Client Responsibilities</h2>
                <p className="text-white/70 leading-relaxed text-lg font-medium tracking-tight">
                  Clients are responsible for:
                </p>
                <ul className="space-y-4 text-white/70 text-lg font-medium tracking-tight">
                  <li className="flex gap-4"><span className="text-[#BEF264]">•</span> Providing clear instructions and assets</li>
                  <li className="flex gap-4"><span className="text-[#BEF264]">•</span> Ensuring they have the rights to all submitted content</li>
                  <li className="flex gap-4"><span className="text-[#BEF264]">•</span> Giving feedback within agreed timelines</li>
                </ul>
                <p className="text-white/70 leading-relaxed text-lg font-medium tracking-tight italic">
                  Delays in feedback may affect delivery schedules.
                </p>
              </section>

              <section className="space-y-6">
                <h2 className="text-2xl font-black text-white uppercase tracking-tight italic">Revisions</h2>
                <p className="text-white/70 leading-relaxed text-lg font-medium tracking-tight">
                  Reasonable revisions are included unless otherwise agreed. Excessive or out-of-scope revisions may incur additional fees.
                </p>
              </section>

              <section className="space-y-6">
                <h2 className="text-2xl font-black text-white uppercase tracking-tight italic">Payments</h2>
                <p className="text-white/70 leading-relaxed text-lg font-medium tracking-tight">
                  Paid services are billed according to the agreed subscription or project terms. Late payments may result in paused services.
                </p>
              </section>

              <section className="space-y-6">
                <h2 className="text-2xl font-black text-white uppercase tracking-tight italic">Intellectual Property</h2>
                <ul className="space-y-4 text-white/70 text-lg font-medium tracking-tight">
                  <li className="flex gap-4"><span className="text-[#BEF264]">•</span> Clients retain ownership of their original content</li>
                  <li className="flex gap-4"><span className="text-[#BEF264]">•</span> Final edited videos become the client’s property once payment is complete</li>
                  <li className="flex gap-4"><span className="text-[#BEF264]">•</span> Living Media retains the right to showcase work for portfolio purposes unless otherwise agreed</li>
                </ul>
              </section>

              <section className="space-y-6">
                <h2 className="text-2xl font-black text-white uppercase tracking-tight italic">Limitation of Liability</h2>
                <p className="text-white/70 leading-relaxed text-lg font-medium tracking-tight">
                  Living Media is not liable for indirect, incidental, or consequential damages arising from the use of our services. We do not guarantee specific results or performance outcomes.
                </p>
              </section>

              <section className="space-y-6">
                <h2 className="text-2xl font-black text-white uppercase tracking-tight italic">Termination</h2>
                <p className="text-white/70 leading-relaxed text-lg font-medium tracking-tight">
                  Either party may terminate services with notice in accordance with the agreed terms. Any completed work up to termination remains payable.
                </p>
              </section>

              <section className="space-y-6">
                <h2 className="text-2xl font-black text-white uppercase tracking-tight italic">Changes to These Terms</h2>
                <p className="text-white/70 leading-relaxed text-lg font-medium tracking-tight">
                  We may update these Terms & Conditions from time to time. Continued use of our services constitutes acceptance of any changes.
                </p>
              </section>

              <section className="space-y-6">
                <h2 className="text-2xl font-black text-white uppercase tracking-tight italic">Governing Law</h2>
                <p className="text-white/70 leading-relaxed text-lg font-medium tracking-tight">
                  These terms are governed by applicable international commercial principles, without reference to a specific jurisdiction.
                </p>
              </section>
            </div>
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsConditions;